/**
 * MissionControlWebSocketContext - Shared WebSocket connection for Mission Control events
 *
 * Provides a single, stable WebSocket connection to /ws/mission-control that all
 * components can subscribe to. This prevents multiple connections and ensures
 * events are reliably delivered.
 *
 * @example
 * ```tsx
 * // In App.tsx or a high-level layout:
 * <MissionControlWebSocketProvider>
 *   <App />
 * </MissionControlWebSocketProvider>
 *
 * // In any component:
 * const { subscribe, isConnected } = useMissionControlEvents();
 *
 * useEffect(() => {
 *   return subscribe({
 *     onTaskStatusChanged: (payload) => {
 *       if (payload.task_id === taskId) refetch();
 *     },
 *   });
 * }, [taskId, subscribe]);
 * ```
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  buildWebSocketUrl,
  canAttemptWebSocketAuth,
  isWorkOsSessionExpiredWebSocketClose,
  shouldReconnectAfterWebSocketClose,
} from "@/utils/wsAuth";
import { getWebSocketBaseUrl } from "@/utils/websocket";
import { useAuth } from "./AuthContext";
import type {
  MissionSummary,
  TaskActionItem,
  InteractionStep,
  ThreadMessage,
} from "@/services/api/agentic-v2";
import type { V2AuditScopePayload } from "@/services/api/guardiansv2/types";
import { parseJsonObject, type JsonValue } from "@/components/guardians/jsonValue";
import {
  parseTriggerCreatedPayload,
  parseTriggerStatusChangedEvent,
  parseV2InteractionCompletedPayload,
  parseV2InteractionProgressPayload,
  parseV2InteractionStartedPayload,
  parseV2MeetingCreatedPayload,
  parseV2MeetingUpdatedPayload,
  parseV2MissionCreatedPayload,
  parseV2MissionCompletedPayload,
  parseV2MissionStageChangedPayload,
  parseV2TaskCreatedPayload,
  parseV2TaskNeedsAttentionPayload,
  parseV2TaskStatusChangedPayload,
  wsEnvelopeCompanyId,
  wsEnvelopePayload,
  wsEnvelopeTrigger,
} from "./missionControlWsParse";
import {
  parseV2AlertRaisedPayload,
  parseV2AuditCompletedPayload,
  parseV2AuditQueuedPayload,
  parseV2AuditRunningPayload,
  parseV2AuditTicketRaisedPayload,
} from "@/components/guardians/shared/auditWsParse";
import { wsLog, wsWarn } from "@/utils/debug";

/** Why a task is blocked by a soft gate. */
export interface GateBlockedReason {
  path: string;
  op: string;
  expected?: JsonValue;
  actual?: JsonValue;
}

/** Human-readable copy for a gate-blocked task's banner. */
export interface GateBlockedDisplay {
  title: string;
  body: string;
}

// ============================================================================
// Type Definitions
// ============================================================================

/** Entity reference in v2 event payloads. */
export interface V2EntityRef {
  entity_type: string;
  entity_id: string;
  display_name?: string | null;
  lead_detail?: string | null;
  lead_status?: string | null;
}

/** Agent reference in v2 event payloads. */
export interface V2AgentRef {
  id: string;
  name: string;
  role?: string | null;
}

/** Payload for v2.mission.created event. */
export interface V2MissionCreatedPayload {
  mission_id: string;
  template_id: string;
  template_name: string | null;
  name: string | null;
  current_stage: string;
  human_owner: string | null;
  status: string;
  entities: V2EntityRef[];
  mission?: MissionSummary | null;
}

/** Payload for v2.mission.stage_changed event. */
export interface V2MissionStageChangedPayload {
  mission_id: string;
  mission_name: string | null;
  old_stage: string | null;
  new_stage: string;
  mission?: MissionSummary | null;
}

/** Payload for v2.mission.completed event. */
export interface V2MissionCompletedPayload {
  mission_id: string;
  mission_name: string | null;
  status: string;
  mission?: MissionSummary | null;
}

/** Hydrated Mission Control mission row on WS payloads. */
export type V2WsMissionRow = MissionSummary;

/** Hydrated Mission Control task row on WS payloads. */
export type V2WsTaskRow = TaskActionItem;

/** Payload for v2.task.created event. */
export interface V2TaskCreatedPayload {
  task_id: string;
  mission_id: string | null;
  name: string;
  status: string;
  task_type: string;
  catalogue_id: string | null;
  assigned_agent_id: string | null;
  agent: V2AgentRef | null;
  entity: V2EntityRef | null;
  spawned_from: "mission_bootstrap" | "branching" | "manual" | string | null;
  spawned_from_node: string | null;
  /** Matches `MissionTaskSummary.stage` when present (for clients patching from WS). */
  stage?: string | null;
  gate_blocked_reason: GateBlockedReason | null;
  task?: V2WsTaskRow | null;
}

/** Payload for v2.task.status_changed event. */
export interface V2TaskStatusChangedPayload {
  task_id: string;
  mission_id: string | null;
  name: string;
  old_status: string | null;
  new_status: string;
  action: string | null;
  agent: V2AgentRef | null;
  entity: V2EntityRef | null;
  /** Matches ``MissionTaskSummary.stage`` when present (for timeline cache patches). */
  stage?: string | null;
  spawned_from_node?: string | null;
  task?: V2WsTaskRow | null;
  mission?: V2WsMissionRow | null;
}

/** Payload for v2.task.needs_attention event. */
export interface V2TaskNeedsAttentionPayload {
  task_id: string;
  mission_id: string | null;
  name: string;
  status: string;
  reason: string | null;
  blockers: string[] | null;
  gate_blocked_reason: GateBlockedReason | null;
  gate_blocked_display: GateBlockedDisplay | null;
  agent: V2AgentRef | null;
  entity: V2EntityRef | null;
  mission?: V2WsMissionRow | null;
}
export interface V2InteractionStartedPayload {
  interaction_id: string;
  task_id: string | null;
  mission_id: string | null;
  agent_id: string | null;
}

/** Payload for v2.interaction.completed event. */
export interface V2InteractionCompletedPayload {
  interaction_id: string;
  task_id: string | null;
  mission_id: string | null;
  agent_id: string | null;
  status: "completed" | "failed" | string;
}

/** Mid-interaction activity phase for live task-thread feedback. */
export type V2InteractionPhase =
  | "thinking"
  | "tool_call"
  | "tool_result"
  | "message";

/** Payload for v2.interaction.progress event. */
export interface V2InteractionProgressPayload {
  interaction_id: string;
  task_id: string | null;
  mission_id: string | null;
  agent_id: string | null;
  phase: V2InteractionPhase;
  tool: string | null;
  step_index: number | null;
  ok: boolean | null;
  label: string | null;
  /** True when new thread rows (steps/messages) were persisted and a refetch is warranted. */
  thread_dirty: boolean;
  step?: InteractionStep | null;
  message?: ThreadMessage | null;
}

/** Payload for v2.meeting.created event. */
export interface V2MeetingCreatedPayload {
  meetingId: string;
  companyId: string;
  title: string;
  status: string;
}

/** Payload for v2.meeting.updated event. */
export interface V2MeetingUpdatedPayload {
  meetingId: string;
  companyId: string;
  title?: string;
  status?: string;
  currentTaskId?: string | null;
  deleted?: boolean;
}

/** Subject reference on guardians v2 WebSocket audit events. */
export interface V2AuditSubjectPayload {
  type: string;
  id: string;
  version?: number | null;
}

/** Payload for v2.audit.queued event. */
export interface V2AuditQueuedPayload {
  audit_id: string;
  audit_type: string;
  subject?: V2AuditSubjectPayload;
  scope: V2AuditScopePayload;
}

/** Payload for v2.audit.running event. */
export interface V2AuditRunningPayload {
  audit_id: string;
  audit_type: string;
  subject?: V2AuditSubjectPayload;
  scope: V2AuditScopePayload;
}

/** Payload for v2.audit.completed event. */
export interface V2AuditCompletedPayload {
  audit_id: string;
  audit_type: string;
  subject?: V2AuditSubjectPayload;
  scope: V2AuditScopePayload;
  rating: string | null;
  score: number | null;
  ticket_count: number;
}

/** Payload for v2.audit.ticket_raised event. */
export interface V2AuditTicketRaisedPayload {
  ticket_id: string;
  ticket_ref: string;
  severity: string;
  type: string;
  source_guardian: string;
  subject?: V2AuditSubjectPayload;
  scope: V2AuditScopePayload;
}

/** Payload for v2.alert.raised event. */
export interface V2AlertRaisedPayload {
  alert_id: string;
  source: string;
  title: string;
  summary: string;
  description?: string | null;
  category?: string | null;
  audience?: string | null;
  next_step?: string | null;
  notify?: boolean;
  subject?: V2AuditSubjectPayload;
  scope: V2AuditScopePayload;
}

/** Legacy trigger created payload. */
export interface TriggerCreatedPayload {
  id: string;
  template_id: string;
  template_name: string | null;
  pod_instance_id: string;
  pod_instance_name: string | null;
  trigger_type: string;
  trigger_source: string | null;
  status: string;
  interaction_id: string | null;
  created_at: string;
  documents_created: number;
  pod_instance_status?: string | null;
}

/** Legacy trigger status changed event. */
export interface TriggerStatusChangedEvent {
  type: "trigger_status_changed";
  company_id: string;
  trigger_id: string;
  pod_instance_id: string;
  interaction_id: string;
  old_state: string | null;
  new_state: string;
  updated_at: string;
  pod_instance_status?: string | null;
}

/** Subscription options for event callbacks. */
export interface MissionControlEventSubscription {
  onTriggerCreated?: (payload: TriggerCreatedPayload) => void;
  onTriggerStatusChanged?: (event: TriggerStatusChangedEvent) => void;
  onMissionCreated?: (payload: V2MissionCreatedPayload) => void;
  onMissionStageChanged?: (payload: V2MissionStageChangedPayload) => void;
  onMissionCompleted?: (payload: V2MissionCompletedPayload) => void;
  onTaskCreated?: (payload: V2TaskCreatedPayload) => void;
  onTaskStatusChanged?: (payload: V2TaskStatusChangedPayload) => void;
  onTaskNeedsAttention?: (payload: V2TaskNeedsAttentionPayload) => void;
  onInteractionStarted?: (payload: V2InteractionStartedPayload) => void;
  onInteractionCompleted?: (payload: V2InteractionCompletedPayload) => void;
  onInteractionProgress?: (payload: V2InteractionProgressPayload) => void;
  onMeetingCreated?: (payload: V2MeetingCreatedPayload) => void;
  onMeetingUpdated?: (payload: V2MeetingUpdatedPayload) => void;
  onAuditQueued?: (payload: V2AuditQueuedPayload) => void;
  onAuditRunning?: (payload: V2AuditRunningPayload) => void;
  onAuditCompleted?: (payload: V2AuditCompletedPayload) => void;
  onAuditTicketRaised?: (payload: V2AuditTicketRaisedPayload) => void;
  onAlertRaised?: (payload: V2AlertRaisedPayload) => void;
}

interface MissionControlWebSocketContextValue {
  isConnected: boolean;
  subscribedCompanyId: string | null;
  /** True when the server subscribed this session to all tenant events (ani staff). */
  subscribedAllTenants: boolean;
  subscribe: (subscription: MissionControlEventSubscription) => () => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getMissionControlWebSocketUrl(): string {
  const baseUrl = getWebSocketBaseUrl() || "ws://localhost:8001";
  const normalizedBase = baseUrl.replace(/\/$/, "");

  let wsUrl = normalizedBase.endsWith("/ws")
    ? `${normalizedBase}/mission-control`
    : `${normalizedBase}/ws/mission-control`;

  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    wsUrl.startsWith("ws://")
  ) {
    wsUrl = wsUrl.replace("ws://", "wss://");
  }

  return buildWebSocketUrl(wsUrl);
}

// ============================================================================
// Context
// ============================================================================

const MissionControlWebSocketContext =
  createContext<MissionControlWebSocketContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function MissionControlWebSocketProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { isAuthenticated, webSocketAuthReady, isLoading, refreshUser } = useAuth();
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;
  const [isConnected, setIsConnected] = useState(false);
  const [subscribedCompanyId, setSubscribedCompanyId] = useState<string | null>(
    null
  );
  const [subscribedAllTenants, setSubscribedAllTenants] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const hadOpenedRef = useRef(false);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscriptionsRef = useRef<Set<MissionControlEventSubscription>>(
    new Set()
  );

  const notifySubscribers = useCallback(
    <K extends keyof MissionControlEventSubscription>(
      eventKey: K,
      payload: Parameters<NonNullable<MissionControlEventSubscription[K]>>[0]
    ) => {
      subscriptionsRef.current.forEach((sub) => {
        const handler = sub[eventKey];
        if (handler) {
          (handler as (p: typeof payload) => void)(payload);
        }
      });
    },
    []
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const raw = typeof event.data === "string" ? event.data : "";
      const data = parseJsonObject(raw);
      if (!data) {
        console.error("[MissionControlWS] Failed to parse message");
        return;
      }

      const messageType = data.type;
      if (typeof messageType !== "string") return;

      // Route v2 events
      if (messageType.startsWith("v2.")) {
        const payload = wsEnvelopePayload(data);
        switch (messageType) {
          case "v2.mission.created": {
            const parsed = parseV2MissionCreatedPayload(payload);
            if (parsed) notifySubscribers("onMissionCreated", parsed);
            break;
          }
          case "v2.mission.stage_changed": {
            const parsed = parseV2MissionStageChangedPayload(payload);
            if (parsed) notifySubscribers("onMissionStageChanged", parsed);
            break;
          }
          case "v2.mission.completed": {
            const parsed = parseV2MissionCompletedPayload(payload);
            if (parsed) notifySubscribers("onMissionCompleted", parsed);
            break;
          }
          case "v2.task.created": {
            const parsed = parseV2TaskCreatedPayload(payload);
            if (parsed) notifySubscribers("onTaskCreated", parsed);
            break;
          }
          case "v2.task.status_changed": {
            const parsed = parseV2TaskStatusChangedPayload(payload);
            if (parsed) notifySubscribers("onTaskStatusChanged", parsed);
            break;
          }
          case "v2.task.needs_attention": {
            const parsed = parseV2TaskNeedsAttentionPayload(payload);
            if (parsed) notifySubscribers("onTaskNeedsAttention", parsed);
            break;
          }
          case "v2.interaction.started": {
            const parsed = parseV2InteractionStartedPayload(payload);
            if (parsed) notifySubscribers("onInteractionStarted", parsed);
            break;
          }
          case "v2.interaction.completed": {
            const parsed = parseV2InteractionCompletedPayload(payload);
            if (parsed) notifySubscribers("onInteractionCompleted", parsed);
            break;
          }
          case "v2.interaction.progress": {
            const parsed = parseV2InteractionProgressPayload(payload);
            if (parsed) notifySubscribers("onInteractionProgress", parsed);
            break;
          }
          case "v2.meeting.created": {
            const parsed = parseV2MeetingCreatedPayload(payload);
            if (parsed) notifySubscribers("onMeetingCreated", parsed);
            break;
          }
          case "v2.meeting.updated": {
            const parsed = parseV2MeetingUpdatedPayload(payload);
            if (parsed) notifySubscribers("onMeetingUpdated", parsed);
            break;
          }
          case "v2.audit.queued": {
            const parsed = parseV2AuditQueuedPayload(payload);
            if (parsed) notifySubscribers("onAuditQueued", parsed);
            break;
          }
          case "v2.audit.running": {
            const parsed = parseV2AuditRunningPayload(payload);
            if (parsed) notifySubscribers("onAuditRunning", parsed);
            break;
          }
          case "v2.audit.completed": {
            const parsed = parseV2AuditCompletedPayload(payload);
            if (parsed) notifySubscribers("onAuditCompleted", parsed);
            break;
          }
          case "v2.audit.ticket_raised": {
            const parsed = parseV2AuditTicketRaisedPayload(payload);
            if (parsed) notifySubscribers("onAuditTicketRaised", parsed);
            break;
          }
          case "v2.alert.raised": {
            const parsed = parseV2AlertRaisedPayload(payload);
            if (parsed) notifySubscribers("onAlertRaised", parsed);
            break;
          }
        }
        return;
      }

      // Legacy and control events
      switch (messageType) {
        case "trigger_created": {
          const parsed = parseTriggerCreatedPayload(wsEnvelopeTrigger(data));
          if (parsed) notifySubscribers("onTriggerCreated", parsed);
          break;
        }
        case "trigger_status_changed": {
          const parsed = parseTriggerStatusChangedEvent(data);
          if (parsed) notifySubscribers("onTriggerStatusChanged", parsed);
          break;
        }
        case "ping":
          wsRef.current?.send(JSON.stringify({ action: "pong" }));
          break;
        case "subscribed": {
          const companyId = wsEnvelopeCompanyId(data);
          if (companyId) {
            setSubscribedCompanyId(companyId);
            const allTenants = data.all_tenants === true;
            setSubscribedAllTenants(allTenants);
            wsLog(
              `[MissionControlWS] Subscribed company ${companyId}` +
                (allTenants ? " (all tenants)" : "")
            );
          }
          break;
        }
        case "unsubscribed":
          setSubscribedCompanyId(null);
          setSubscribedAllTenants(false);
          break;
      }
    },
    [notifySubscribers]
  );

  const connect = useCallback(() => {
    // Don't connect if already connected or in handshake
    const existing = wsRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    if (!canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) {
      return;
    }

    try {
      hadOpenedRef.current = false;
      const wsUrl = getMissionControlWebSocketUrl();
      wsLog("[MissionControlWS] Connecting to", wsUrl.split("?")[0]);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        wsLog("[MissionControlWS] Connected");
        hadOpenedRef.current = true;
        setIsConnected(true);
        ws.send(JSON.stringify({ action: "subscribe" }));
      };

      ws.onmessage = handleMessage;

      ws.onclose = (event) => {
        wsLog(
          `[MissionControlWS] Disconnected (code: ${event.code}, reason: ${event.reason})`
        );
        setIsConnected(false);
        setSubscribedCompanyId(null);
        setSubscribedAllTenants(false);
        wsRef.current = null;

        if (isWorkOsSessionExpiredWebSocketClose(event.code, event.reason)) {
          void refreshUserRef.current();
        }

        if (
          shouldReconnectAfterWebSocketClose(event.code, event.reason, {
            hadOpened: hadOpenedRef.current,
          })
        ) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 3000);
        }
      };

      ws.onerror = (error) => {
        wsWarn("[MissionControlWS] Error:", error);
      };
    } catch (err) {
      console.error("[MissionControlWS] Failed to create WebSocket:", err);
    }
  }, [handleMessage, isAuthenticated, webSocketAuthReady]);

  // Connect when auth is ready (WorkOS session or legacy JWT)
  useEffect(() => {
    if (isLoading) return;
    if (!canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) return;

    const timer = setTimeout(() => {
      connect();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, isLoading, isAuthenticated, webSocketAuthReady]);

  // Subscribe function for components
  const subscribe = useCallback(
    (subscription: MissionControlEventSubscription) => {
      subscriptionsRef.current.add(subscription);
      return () => {
        subscriptionsRef.current.delete(subscription);
      };
    },
    []
  );

  const value: MissionControlWebSocketContextValue = {
    isConnected,
    subscribedCompanyId,
    subscribedAllTenants,
    subscribe,
  };

  return (
    <MissionControlWebSocketContext.Provider value={value}>
      {children}
    </MissionControlWebSocketContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Access the shared Mission Control WebSocket connection.
 * Must be used within MissionControlWebSocketProvider.
 */
export function useMissionControlEvents(): MissionControlWebSocketContextValue {
  const context = useContext(MissionControlWebSocketContext);
  if (!context) {
    throw new Error(
      "useMissionControlEvents must be used within MissionControlWebSocketProvider"
    );
  }
  return context;
}

/**
 * Convenience hook that subscribes to events and auto-cleans up.
 * Uses a stable subscription object so the WebSocket listener set is not churned on
 * every parent render; handlers always read the latest subscription from a ref.
 */
export function useMissionControlSubscription(
  subscription: MissionControlEventSubscription,
  options: { enabled?: boolean } = {},
): { isConnected: boolean; subscribedCompanyId: string | null; subscribedAllTenants: boolean } {
  const { subscribe, isConnected, subscribedCompanyId, subscribedAllTenants } =
    useMissionControlEvents();
  const enabled = options.enabled ?? true;

  const subRef = useRef(subscription);
  subRef.current = subscription;

  useEffect(() => {
    if (!enabled) return;
    const stable: MissionControlEventSubscription = {
      onTriggerCreated: (p) => subRef.current.onTriggerCreated?.(p),
      onTriggerStatusChanged: (e) => subRef.current.onTriggerStatusChanged?.(e),
      onMissionCreated: (p) => subRef.current.onMissionCreated?.(p),
      onMissionStageChanged: (p) => subRef.current.onMissionStageChanged?.(p),
      onMissionCompleted: (p) => subRef.current.onMissionCompleted?.(p),
      onTaskCreated: (p) => subRef.current.onTaskCreated?.(p),
      onTaskStatusChanged: (p) => subRef.current.onTaskStatusChanged?.(p),
      onTaskNeedsAttention: (p) => subRef.current.onTaskNeedsAttention?.(p),
      onInteractionStarted: (p) => subRef.current.onInteractionStarted?.(p),
      onInteractionCompleted: (p) => subRef.current.onInteractionCompleted?.(p),
      onInteractionProgress: (p) => subRef.current.onInteractionProgress?.(p),
      onMeetingCreated: (p) => subRef.current.onMeetingCreated?.(p),
      onMeetingUpdated: (p) => subRef.current.onMeetingUpdated?.(p),
      onAuditQueued: (p) => subRef.current.onAuditQueued?.(p),
      onAuditRunning: (p) => subRef.current.onAuditRunning?.(p),
      onAuditCompleted: (p) => subRef.current.onAuditCompleted?.(p),
      onAuditTicketRaised: (p) => subRef.current.onAuditTicketRaised?.(p),
      onAlertRaised: (p) => subRef.current.onAlertRaised?.(p),
    };
    return subscribe(stable);
  }, [subscribe, enabled]);

  return { isConnected, subscribedCompanyId, subscribedAllTenants };
}
